import { useCallback, useState } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { moduleCategories } from '../config/moduleCategories';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// The one-switch-per-module keys (not the platform settings).
export const MODULE_SWITCH_KEYS = (moduleCategories.Modules || []).map((m) => m.key);

/**
 * Per-working-company module switches for the selected client.
 *
 * The client row (usePermissions) is the DEFAULT; a working company may carry
 * its own explicit switches which win both ways. `selectedWorkingCompany`
 * '' means "client default" — the existing client-level save applies.
 */
export const useWorkingCompanyModules = (showMessage) => {
  const [companies, setCompanies] = useState([]);
  const [clientDefaults, setClientDefaults] = useState(null);
  const [selectedWorkingCompany, setSelectedWorkingCompany] = useState('');
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const draftFor = (companyEntry, defaults) =>
    Object.fromEntries(
      MODULE_SWITCH_KEYS.map((k) => [k, Boolean(companyEntry?.effective?.[k] ?? defaults?.[k] ?? false)]),
    );

  const fetchWorkingCompanies = useCallback(async (clientName, keepSelection = false) => {
    if (!clientName) return;
    try {
      setLoading(true);
      const res = await apiFetch(
        `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}/working-companies`,
        { method: 'GET' },
      );
      const json = await res.json();
      const data = json?.data || { clientDefaults: null, companies: [] };
      setCompanies(data.companies || []);
      setClientDefaults(data.clientDefaults || null);
      setSelectedWorkingCompany((prev) => {
        const next = keepSelection ? prev : '';
        const entry = (data.companies || []).find((c) => c.workingCompany === next);
        setDraft(next ? draftFor(entry, data.clientDefaults) : {});
        return next;
      });
    } catch (error) {
      console.error('Failed to load working companies:', error);
      showMessage('error', 'Failed to load working companies');
    } finally {
      setLoading(false);
    }
  }, [showMessage]);

  const selectWorkingCompany = (name) => {
    setSelectedWorkingCompany(name);
    const entry = companies.find((c) => c.workingCompany === name);
    setDraft(name ? draftFor(entry, clientDefaults) : {});
  };

  const toggleDraft = (key) => setDraft((prev) => ({ ...prev, [key]: !prev[key] }));

  const saveWorkingCompany = async (clientName) => {
    if (!clientName || !selectedWorkingCompany) return;
    try {
      setSaving(true);
      const res = await apiFetch(
        `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}/working-companies/${encodeURIComponent(selectedWorkingCompany)}`,
        { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(draft) },
      );
      if (!res.ok) {
        showMessage('error', 'Failed to save working company modules');
      } else {
        showMessage('success', `Modules saved for ${selectedWorkingCompany}`);
      }
      await fetchWorkingCompanies(clientName, true);
    } catch (error) {
      console.error('Failed to save working company modules:', error);
      showMessage('error', 'Failed to save working company modules');
    } finally {
      setSaving(false);
    }
  };

  const resetWorkingCompany = async (clientName) => {
    if (!clientName || !selectedWorkingCompany) return;
    try {
      setSaving(true);
      const res = await apiFetch(
        `${BASE_URL}/api/configurations/${encodeURIComponent(clientName)}/working-companies/${encodeURIComponent(selectedWorkingCompany)}`,
        { method: 'DELETE' },
      );
      if (!res.ok) showMessage('error', 'Failed to reset working company');
      else showMessage('success', `${selectedWorkingCompany} now follows the client default`);
      await fetchWorkingCompanies(clientName, true);
    } catch (error) {
      console.error('Failed to reset working company modules:', error);
      showMessage('error', 'Failed to reset working company');
    } finally {
      setSaving(false);
    }
  };

  const selectedEntry = companies.find((c) => c.workingCompany === selectedWorkingCompany) || null;

  return {
    companies,
    clientDefaults,
    selectedWorkingCompany,
    selectedEntry,
    draft,
    loadingWorkingCompanies: loading,
    savingWorkingCompany: saving,
    fetchWorkingCompanies,
    selectWorkingCompany,
    toggleDraft,
    saveWorkingCompany,
    resetWorkingCompany,
  };
};
